import { Box, Grid } from "@mui/material";
import React, { useEffect, useState, useMemo, useCallback } from "react";
import Typography from '@mui/material/Typography';
import _ from "lodash";
import TextButtonComponet from "../../atom/Buttons/TextButton";
import moment from 'moment';
import { DocumentCard } from "../../molecule";
import DocumentBreadcrumbs from "../../molecule/Document/BreadCrumb";
import { useNavigate, useLocation } from "react-router-dom";
import { baseappURL } from "../../../core/repository/Repository";
import DocumentRepository from '../../../core/repository/DocumentRepository';
import CircularProgress from "@mui/material/CircularProgress";
import { checkIsImageFile } from "../../../core/Constant";

const IS_DIRECTORY_FOLDER = 1;
const ROOT_BREADCRUMB = { id: null, name: "Smart Docs" };
const normalizeParentId = (parentId) => parentId == null || parentId === 0 ? null : Number(parentId);

const TabItemFive = ({ itemFiveData }) => {
    const navigate = useNavigate();
    const location = useLocation();

    const courseId = location?.state?.course_id || location?.state?.course?.course_id || null;
    const courseName = useMemo(() => location?.state?.course?.name || location?.state?.course_name || "", [location]);

    const storageKey = useMemo(() => `tab_item_five_state_${courseId || "default"}`, [courseId]);

    const [currentFolderId, setCurrentFolderId] = useState(() => {
        const returnFolderId = location?.state?.returnTabState?.currentFolderId;
        if (returnFolderId !== undefined) return returnFolderId;

        const saved = sessionStorage.getItem(storageKey);
        if (saved) {
            try { return JSON.parse(saved).currentFolderId ?? null; } catch (e) { }
        }
        return null;
    });

    const [folderStack, setFolderStack] = useState(() => {
        const returnStack = location?.state?.returnTabState?.folderStack;
        if (returnStack !== undefined) return returnStack;

        const saved = sessionStorage.getItem(storageKey);
        if (saved) {
            try { return JSON.parse(saved).folderStack || []; } catch (e) { }
        }
        return [];
    });

    const [documents, setDocuments] = useState(itemFiveData?.documents || []);
    const [folderLoading, setFolderLoading] = useState(false);

    useEffect(() => {
        sessionStorage.setItem(
            storageKey,
            JSON.stringify({ currentFolderId, folderStack })
        );
    }, [currentFolderId, folderStack, storageKey]);

    const fetchDocuments = useCallback(async (parentId = null) => {
        try {
            if (!courseId) return;
            setFolderLoading(true);
            const params = parentId != null && parentId !== 0 ? { course_id: courseId, parent_id: parentId } : { course_id: courseId };
            const res = await DocumentRepository.getDocumentList(params);
            const docs = res?.documents || res?.data?.documents || [];
            const filtered = docs.filter(d => d.status === 1);
            setDocuments(filtered);
        } catch (e) {
            console.error(e);
        } finally {
            setFolderLoading(false);
        }
    }, [courseId]);

    useEffect(() => {
        if (currentFolderId !== null) {
            fetchDocuments(currentFolderId);
        } else if (itemFiveData?.documents) {
            setDocuments(itemFiveData.documents);
        } else {
            fetchDocuments(null);
        }
    }, [itemFiveData, currentFolderId, fetchDocuments]);

    const openFolder = async (doc) => {
  if (folderLoading) return;

    setCurrentFolderId(doc.id);

    setFolderStack((prev) => {
      const lastFolder = prev[prev.length - 1];
      if (lastFolder?.id === doc.id) {
        return prev;
      }
      return [...prev, { id: doc.id, name: doc.name }];
    });

    fetchDocuments(doc.id);
};

    const navigateToBreadcrumb = (index) => {
        if (index === 0 || (courseName && index === 1)) {
            setCurrentFolderId(null);
            setFolderStack([]);
            fetchDocuments(null);
            return;
        }
        const folderIndex = index - (courseName ? 2 : 1);
        const newStack = folderStack.slice(0, folderIndex + 1);
        const folderId = newStack[folderIndex]?.id ?? null;

        setFolderStack(newStack);
        setCurrentFolderId(folderId);
        fetchDocuments(folderId);
    };

    const breadcrumbTrail = useMemo(() => {
        const trail = [ROOT_BREADCRUMB];
        if (courseName) trail.push({ id: `course-${courseId}`, name: courseName });
        return [...trail, ...folderStack];
    }, [courseName, courseId, folderStack]);

    const isFolder = (doc) => Number(doc?.is_directory) === IS_DIRECTORY_FOLDER;
    const formatTitle = (name = "") => name.replace(/\.[^/.]+$/, "");

    const handleCardAction = (item) => {
        if (isFolder(item)) {
            openFolder(item);
            return;
        }
        navigate('/view-document', {
            state: {
                document: item,
                baseurl: baseappURL,
                returnPath: location?.pathname || "/",
                returnState: {
                    ...(location?.state || {}),
                    returnTabIndex: 3,
                    returnTabState: { currentFolderId, folderStack },
                },
            },
        });
    };

    const displayList = useMemo(() => {
        let items = documents || [];
        const hasParentIds = items.some(d => d.parent_id != null && d.parent_id !== "" && d.parent_id !== 0);
        if (hasParentIds) {
            const parentId = currentFolderId ?? null;
            items = items.filter((d) => {
                const docParentId = normalizeParentId(d.parent_id);
                if (parentId != null) return docParentId === normalizeParentId(parentId);
                return docParentId === null || docParentId === normalizeParentId(courseId);
            });
        }
        const folders = items.filter(isFolder);
        const files = items.filter((d) => !isFolder(d));
        return [...folders, ...files];
    }, [documents, currentFolderId, courseId]);

    return (
        <Grid container rowGap={2} columnGap={3}>
            <Grid item xs={12}>
                {/* RENDERED SHARED BREADCRUMBS COMPONENT */}
                <DocumentBreadcrumbs 
                    breadcrumbTrail={breadcrumbTrail} 
                    onBreadcrumbClick={navigateToBreadcrumb} 
                />
            </Grid>

            {displayList.length === 0 && !folderLoading ? (
                <span className="no-review-text">Currently, there are no Documents available for this course.</span>
            ) : (
                <>
                    {displayList.map((item, index) => {
                        const folder = isFolder(item);
                        const image = checkIsImageFile(item?.name, folder);

                        return (
                        <DocumentCard 
                            key={item.id || index} 
                            item={item}
                            baseUrl={baseappURL}
                            isFolder={folder}
                            isImage={image}
                            title={formatTitle(item.name)}
                            btnText={folder ? "View Folder" : "View Document"} 
                            onViewDocument={() => handleCardAction(item)} 
                        />
                    );
                        })}
                </>
            )}
            {folderLoading && (
                            <Box
                              sx={{
                                position: "fixed",
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                bgcolor: "rgba(255,255,255,0.4)",
                                zIndex: 9999,
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                              }}
                            >
                              <CircularProgress />
                            </Box>
                          )}
        </Grid>
    )
}

export default TabItemFive;